/**
 * Client-Side Logger
 *
 * Captures logs and errors from the browser and sends them to the API.
 * API Endpoint: POST /log
 */

export type LogLevel = 'info' | 'warn' | 'error';

import { getTraceId, resetTraceId } from './api/traceId';

class RemoteLogger {
    private queue: any[] = [];
    private flushing = false;
    private maxBatchSize = 10;
    private flushInterval = 5000;

    constructor() {
        // Auto-flush occasionally
        setInterval(() => this.flush(), this.flushInterval);
    }

    /**
     * Get or set the current traceId
     */
    getTraceId() {
        return getTraceId();
    }

    resetTrace() {
        return resetTraceId();
    }

    log(level: LogLevel, message: string, metadata?: Record<string, any>) {
        // Console log for local debugging (avoid printing undefined)
        if (level === 'error') {
            metadata !== undefined ? console.error(message, metadata) : console.error(message);
        } else if (level === 'warn') {
            metadata !== undefined ? console.warn(message, metadata) : console.warn(message);
        } else {
            metadata !== undefined ? console.log(message, metadata) : console.log(message);
        }

        // DIAMOND: Queue for remote logging - metadata must be an object, not a string
        const entry = {
            source: 'website',
            level,
            message,
            metadata: {
                ...metadata,
                traceId: getTraceId(),
                url: window.location.href,
                userAgent: navigator.userAgent,
            },
            timestamp: new Date().toISOString(),
        };

        this.queue.push(entry);

        // Immediate flush for errors to ensure capture before crash/reload
        if (level === 'error' || this.queue.length >= this.maxBatchSize) {
            this.flush();
        }
    }

    info(message: string, metadata?: Record<string, any>) {
        this.log('info', message, metadata);
    }

    warn(message: string, metadata?: Record<string, any>) {
        this.log('warn', message, metadata);
    }

    error(message: string, metadata?: Record<string, any>) {
        this.log('error', message, metadata);
    }

    captureError(message: string, error: any, metadata?: Record<string, any>) {
        this.log('error', message, {
            ...metadata,
            originalError: error?.message || String(error),
            stack: error?.stack,
        });
    }

    private async flush() {
        if (this.queue.length === 0 || this.flushing) return;
        this.flushing = true;

        const batch = this.queue.slice(0, this.maxBatchSize);
        this.queue = this.queue.slice(this.maxBatchSize);

        try {
            // Lazy import to avoid circular dependency
            const { api } = await import('./api');
            // Note: api-client doesn't support batch logging yet, so we map
            await Promise.all(
                batch.map((entry) =>
                    // Use headers for trace ID if needed, but we put it in metadata for now
                    api
                        .log(entry)
                        .catch((_e: any) => { })
                )
            );
        } finally {
            this.flushing = false;
            // Retrigger if more args
            if (this.queue.length > 0) setTimeout(() => this.flush(), 1000);
        }
    }
}

export const logger = new RemoteLogger();

/**
 * Initialize Global Error Handlers
 * Call this in main.tsx
 */
export function initGlobalErrorHandlers() {
    window.onerror = (message, source, lineno, colno, error) => {
        logger.error('Uncaught Exception', {
            message: String(message),
            source,
            lineno,
            colno,
            stack: error?.stack,
        });
    };

    window.onunhandledrejection = (event) => {
        logger.error('Unhandled Rejection', {
            reason: event.reason?.message || String(event.reason),
            stack: event.reason?.stack,
        });
    };
}
